import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { GlobalRoleAssignment } from '../../types/permissions/global-role-assignment.type';
import { GlobalRoleItem } from '../../types/permissions/global-role.type';
import { OrgRoleAssignment } from '../../types/permissions/org-role-assignment.type';
import { OrgRoleItem } from '../../types/permissions/org-role.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import SnackbarUtils from '../../utils/SnackbarUtils';
import {
  getGlobalRoleAssignments,
  getGlobalRolesList,
  getOrgRoleAssignments,
  getOrgRolesList,
} from './permissions.action';

export type OrgRolesListRouteParams = {
  page: number;
  limit: number;
  order: string;
  q: string;
};

export type GlobalRolesListRouteParams = {
  page: number;
  limit: number;
  order: string;
  q: string;
};

export type OrgRoleAssignmentsRouteParams = {
  page: number;
  limit: number;
  order: string;
  q: string;
};

export type GlobalRoleAssignmentsRouteParams = {
  page: number;
  limit: number;
  order: string;
  q: string;
};

type PermissionsState = {
  orgRolesListRouteParams: OrgRolesListRouteParams;
  orgRolesListFetchStatus: FETCH_STATUS;
  orgRolesList: PaginationType<OrgRoleItem> | null;

  orgRoleAssignmentsRouteParams: OrgRoleAssignmentsRouteParams;
  orgRoleAssignmentsFetchStatus: FETCH_STATUS;
  orgRoleAssignments: PaginationType<OrgRoleAssignment> | null;

  globalRolesListRouteParams: GlobalRolesListRouteParams;
  globalRolesListFetchStatus: FETCH_STATUS;
  globalRolesList: PaginationType<GlobalRoleItem> | null;

  globalRoleAssignmentsRouteParams: GlobalRoleAssignmentsRouteParams;
  globalRoleAssignmentsFetchStatus: FETCH_STATUS;
  globalRoleAssignments: PaginationType<GlobalRoleAssignment> | null;
};

const initialOrgRolesListRouteParams: OrgRolesListRouteParams = {
  page: 1,
  limit: 20,
  order: '-id',
  q: '',
};

const orgRoleAssignmentsInitialRouteParams: OrgRoleAssignmentsRouteParams = {
  page: 1,
  limit: 20,
  order: 'default',
  q: '',
};

const initialGlobalRolesListRouteParams: GlobalRolesListRouteParams = {
  page: 1,
  limit: 20,
  order: '-id',
  q: '',
};

const globalRoleAssignmentsInitialRouteParams: GlobalRoleAssignmentsRouteParams = {
  page: 1,
  limit: 20,
  order: 'default',
  q: '',
};

const INIT_STATE: PermissionsState = {
  orgRolesListRouteParams: initialOrgRolesListRouteParams,
  orgRolesListFetchStatus: FETCH_STATUS.NULL,
  orgRolesList: null,

  orgRoleAssignmentsRouteParams: orgRoleAssignmentsInitialRouteParams,
  orgRoleAssignmentsFetchStatus: FETCH_STATUS.NULL,
  orgRoleAssignments: null,

  globalRolesListRouteParams: initialGlobalRolesListRouteParams,
  globalRolesListFetchStatus: FETCH_STATUS.NULL,
  globalRolesList: null,

  globalRoleAssignmentsRouteParams: globalRoleAssignmentsInitialRouteParams,
  globalRoleAssignmentsFetchStatus: FETCH_STATUS.NULL,
  globalRoleAssignments: null,
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState: INIT_STATE,
  reducers: {
    setOrgRolesList: (state, action) => {
      state.orgRolesList = action.payload;
    },
    setOrgRolesListFetchStatus: (state, action) => {
      state.orgRolesListFetchStatus = action.payload;
    },
    setOrgRoleAssignments: (state, action) => {
      state.orgRoleAssignments = action.payload;
    },
    setOrgRoleAssignmentsFetchStatus: (state, action) => {
      state.orgRoleAssignmentsFetchStatus = action.payload;
    },
    setGlobalRolesList: (state, action) => {
      state.globalRolesList = action.payload;
    },
    setGlobalRolesListFetchStatus: (state, action) => {
      state.globalRolesListFetchStatus = action.payload;
    },
    setGlobalRoleAssignments: (state, action) => {
      state.globalRoleAssignments = action.payload;
    },
    setGlobalRoleAssignmentsFetchStatus: (state, action) => {
      state.globalRoleAssignmentsFetchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const {
        payload: { reducer, data },
      } = action as ToolkitActionType<{ reducer: string; data: Record<string, unknown> }>;
      switch (reducer) {
        case 'permissions/org-roles':
          state.orgRolesListRouteParams = { ...state.orgRolesListRouteParams, ...data };
          break;
        case 'permissions/global-roles':
          state.globalRolesListRouteParams = { ...state.globalRolesListRouteParams, ...data };
          break;
        case 'permissions/org-role-assignments':
          state.orgRoleAssignmentsRouteParams = { ...state.orgRoleAssignmentsRouteParams, ...data };
          break;
        case 'permissions/global-role-assignments':
          state.globalRoleAssignmentsRouteParams = { ...state.globalRoleAssignmentsRouteParams, ...data };
          break;
      }
    });

    builder
      .addCase(getOrgRolesList.pending, state => {
        state.orgRolesListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getOrgRolesList.rejected, (state, action) => {
        state.orgRolesListFetchStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(getOrgRolesList.fulfilled, (state, action) => {
        state.orgRolesListFetchStatus = FETCH_STATUS.FULFILLED;
        state.orgRolesList = action.payload;
      });

    builder
      .addCase(getOrgRoleAssignments.pending, state => {
        state.orgRoleAssignmentsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getOrgRoleAssignments.rejected, (state, action) => {
        state.orgRoleAssignmentsFetchStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(getOrgRoleAssignments.fulfilled, (state, action) => {
        state.orgRoleAssignmentsFetchStatus = FETCH_STATUS.FULFILLED;
        state.orgRoleAssignments = action.payload;
      });

    builder
      .addCase(getGlobalRolesList.pending, state => {
        state.globalRolesListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getGlobalRolesList.rejected, (state, action) => {
        state.globalRolesListFetchStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(getGlobalRolesList.fulfilled, (state, action) => {
        state.globalRolesListFetchStatus = FETCH_STATUS.FULFILLED;
        state.globalRolesList = action.payload;
      });

    builder
      .addCase(getGlobalRoleAssignments.pending, state => {
        state.globalRoleAssignmentsRouteParams = globalRoleAssignmentsInitialRouteParams;
        state.globalRoleAssignmentsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getGlobalRoleAssignments.rejected, (state, action) => {
        state.globalRoleAssignmentsFetchStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(getGlobalRoleAssignments.fulfilled, (state, action) => {
        state.globalRoleAssignmentsFetchStatus = FETCH_STATUS.FULFILLED;
        state.globalRoleAssignments = action.payload;
      });
  },
});

export const {
  setOrgRolesList,
  setOrgRolesListFetchStatus,
  setOrgRoleAssignments,
  setOrgRoleAssignmentsFetchStatus,
  setGlobalRolesList,
  setGlobalRolesListFetchStatus,
  setGlobalRoleAssignments,
  setGlobalRoleAssignmentsFetchStatus,
} = permissionsSlice.actions;

export default permissionsSlice.reducer;
